import { useRouter } from 'next/compat/router'
import { useCallback, useEffect, useState } from 'react'

// Key for storing invite code in local storage (set on landing with ?invite=...)
const INVITE_CODE_STORAGE_KEY = 'dupe-invite-code'

/**
 * An invite code is 2 uppercase letters, a dash, then 5 digits (e.g. `AB-12345`).
 * Shared so any invite-code entry point and the headless `useAutoRedeemInvite`
 * auto-apply path validate identically.
 */
export const isValidInviteFormat = (code: string): boolean =>
  /^[A-Z]{2}-[0-9]{5}$/.test(code)

export const useInviteCodeFromUrl = () => {
  // next/compat/router: the pages NextRouter when mounted under pages/, null
  // under app/ (where URL cleanup falls back to history.replaceState below).
  const router = useRouter()
  const [inviteCode, setInviteCode] = useState<string>()

  const clearInviteCode = useCallback(() => {
    localStorage.removeItem(INVITE_CODE_STORAGE_KEY)
    setInviteCode(undefined)

    // remove invite code from url (using router)
    if (router) {
      const newQuery = { ...router.query }
      delete newQuery.invite

      router.replace(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    } else {
      // App Router has no shallow router.replace — strip the param via
      // history.replaceState (the documented App Router shallow-routing
      // equivalent).
      const url = new URL(window.location.href)
      if (url.searchParams.has('invite')) {
        url.searchParams.delete('invite')
        window.history.replaceState(null, '', url.toString())
      }
    }
  }, [router, setInviteCode])

  useEffect(() => {
    // Mount-time window read instead of useSearchParams(): this hook is
    // reachable from the always-mounted Layout → AuthModal chain, where
    // useSearchParams() would force a CSR bailout for statically rendered
    // App Router pages. Invite codes arrive on landing (hard load), so a
    // mount-time read is equivalent.
    const code = new URLSearchParams(window.location.search).get('invite')

    if (code) {
      // Store the invite code in localStorage for later use in onboarding
      localStorage.setItem(INVITE_CODE_STORAGE_KEY, code)
      setInviteCode(code)
    }
  }, [setInviteCode])

  return { inviteCode, clearInviteCode }
}

export const getStoredInviteCode = (): string | null => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(INVITE_CODE_STORAGE_KEY)
}

export const clearStoredInviteCode = () => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(INVITE_CODE_STORAGE_KEY)
}

export default useInviteCodeFromUrl
